// landingTokens.ts — single source of truth for all landing page design
// MANDATORY: every landing section imports from here. Never hardcode values.

// ─── COLORS ───────────────────────────────────────────────
export const colors = {
  student:       '#a855f7',
  counselor:     '#ec4899',
  college:       '#fbbf24',
  background:    '#050508',
  surface:       'rgba(255,255,255,0.03)',
  textPrimary:   '#FFFFFF',
  textSecondary: 'rgba(255,255,255,0.65)',
  textMuted:     'rgba(255,255,255,0.4)',
  border:        'rgba(255,255,255,0.08)',
}

// ─── CARD STYLES (per portal) ─────────────────────────────
export const cardStyles = {
  student: {
    border:     '1px solid rgba(168,85,247,0.25)',
    background: 'rgba(168,85,247,0.04)',
    color:      '#a855f7',
  },
  counselor: {
    border:     '1px solid rgba(236,72,153,0.25)',
    background: 'rgba(236,72,153,0.04)',
    color:      '#ec4899',
  },
  college: {
    border:     '1px solid rgba(251,191,36,0.25)',
    background: 'rgba(251,191,36,0.04)',
    color:      '#fbbf24',
  },
}

// ─── TYPOGRAPHY SCALE ─────────────────────────────────────
// Same font families throughout. Size + weight vary by context.
// Serif = Cormorant Garamond (hero titles, section headings)
// Sans  = Inter (everything else)

export const typography = {

  // DISPLAY — hero headlines only
  // Biggest. Most dramatic. Used once per page.
  display: {
    fontFamily:    'Cormorant Garamond, serif',
    fontSize:      'clamp(44px, 6vw, 92px)',
    fontWeight:    800,
    color:         '#FFFFFF',
    letterSpacing: '-0.02em',
    lineHeight:    1.0,
  },

  // HEADING XL — section opening statement
  // "The system is broken. For everyone."
  headingXL: {
    fontFamily:    'Cormorant Garamond, serif',
    fontSize:      'clamp(36px, 4vw, 64px)',
    fontWeight:    700,
    color:         '#FFFFFF',
    letterSpacing: '-0.02em',
    lineHeight:    1.1,
  },

  // HEADING LG — sub-section heading
  // "What each side gets"
  headingLG: {
    fontFamily:    'Cormorant Garamond, serif',
    fontSize:      'clamp(28px, 3vw, 48px)',
    fontWeight:    700,
    color:         '#FFFFFF',
    letterSpacing: '-0.01em',
    lineHeight:    1.15,
  },

  // HEADING MD — card titles, feature names
  // "Lost in the noise"
  headingMD: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      'clamp(18px, 1.5vw, 22px)',
    fontWeight:    700,
    color:         '#FFFFFF',
    lineHeight:    1.2,
  },

  // SUBHEADING — supporting text under section headings
  subheading: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      'clamp(15px, 1.2vw, 18px)',
    fontWeight:    400,
    color:         'rgba(255,255,255,0.65)',
    lineHeight:    1.6,
  },

  // LABEL — STUDENTS / COUNSELORS / COLLEGES tags
  label: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      '11px',
    fontWeight:    600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase' as const,
    lineHeight:    1,
  },

  // BODY — paragraph text, bullet points
  body: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      '14px',
    fontWeight:    400,
    color:         'rgba(255,255,255,0.6)',
    lineHeight:    1.6,
  },

  // CAPTION — small muted text, social proof, footnotes
  caption: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      '12px',
    fontWeight:    400,
    color:         'rgba(255,255,255,0.35)',
    lineHeight:    1.5,
    letterSpacing: '0.03em',
  },

  // STAT — big impact numbers like "400:1" or "12,000+"
  stat: {
    fontFamily:    'Inter, sans-serif',
    fontSize:      'clamp(48px, 6vw, 96px)',
    fontWeight:    800,
    color:         '#FFFFFF',
    letterSpacing: '-0.03em',
    lineHeight:    1.0,
  },
}

// ─── MOTION ───────────────────────────────────────────────
export const motion = {
  ease:    [0.22, 1, 0.36, 1] as const,
  fast:    { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const },
  normal:  { duration: 0.6,  ease: [0.22, 1, 0.36, 1] as const },
  slow:    { duration: 0.8,  ease: [0.22, 1, 0.36, 1] as const },
  stagger: 0.08,
}

// ─── SECTION SPACING ──────────────────────────────────────
export const spacing = {
  sectionY:     '120px',
  sectionYSM:   '80px',
  containerMax: '1200px',
  containerPad: '24px',
}
